package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.LBookOpenIcon
import dev.lingvo.site.ds.LBracesIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChord
import dev.lingvo.site.ds.LIconBadge
import dev.lingvo.site.ds.LKeyboardIcon
import dev.lingvo.site.ds.LMailIcon
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LShieldIcon
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.commandKeyLabel
import dev.lingvo.site.ds.copySafely
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t

/** How long "Copied" stands before the button offers to copy again. */
private const val COPY_FEEDBACK_MS = 2_000L

/**
 * Where somebody goes when it is not working.
 *
 * **There was no such place.** An import that failed, a translation that looked wrong, a charge
 * nobody could explain — the app offered no route to a human, and the support address existed only
 * in the terms and the refund policy, on the marketing site, where somebody already inside the app
 * would never look for it.
 *
 * **This page makes no API calls, deliberately.** The commonest reason to open it is that the API
 * is not answering, and a help page that needs the thing that is broken is not help. Everything on
 * it comes from the session already in memory, the browser, and the build — so it renders during an
 * outage, which is exactly when it is wanted.
 */
@Page("/app/help")
@Layout(".components.layouts.AppLayout")
@Composable
fun HelpPage() {
    val palette = ColorMode.current.toSitePalette()
    val scope = rememberCoroutineScope()
    val user = (Session.state as? SessionState.Authenticated)?.user
    val workspace = WorkspaceSelection.current()
    var copied by remember { mutableStateOf(false) }
    var copyFailed by remember { mutableStateOf(false) }

    // The facts a support reply always has to ask for. Assembled here so nobody has to describe
    // their own setup from memory — and deliberately narrow: no tokens, no provider keys, no
    // project contents. Only what identifies the account and the page.
    val diagnostics = buildString {
        appendLine("Workspace: ${workspace?.name ?: "—"} (${workspace?.slug ?: "—"})")
        appendLine("Plan: ${workspace?.plan?.name ?: "—"}")
        appendLine("Role: ${workspace?.role?.name ?: "—"}")
        appendLine("Account: ${user?.email ?: "—"}")
        appendLine("Page: ${window.location.pathname}")
        appendLine("Browser: ${window.navigator.userAgent}")
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        // **Reaching a person is the one thing this page exists for**, so it is the page's single
        // primary action and it sits in the header rather than four paragraphs down beside a
        // secondary "Copy these details".
        PageHeader(
            t("app.help.help", "Help"),
            t("app.help.how_things_work_and_how_to", "How things work, and how to reach a person when they do not."),
        ) {
            LButton(
                t("app.help.email_support", "Email support"),
                {
                    val subject = "Lingvo — ${workspace?.slug ?: "help"}"  // i18n-ignore — a product name and a workspace slug, no sentence a translator could change
                    window.location.href = "mailto:support@lingvo.dev?subject=" +
                        encodeURIComponent(subject)
                },
                leadingIcon = { LMailIcon() },
            )
        }

        // The four chords, near the top, where somebody looking for one will find it without
        // scrolling past the support address. `LChord` draws each as its own key cap and says
        // " then " between them to a screen reader, which a run of bare `LKbd`s did not.
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionTitle(t("app.help.keyboard", "Keyboard"), icon = { LKeyboardIcon() })
                Shortcut(listOf(commandKeyLabel, "K"), t("app.help.search_and_jump", "Search and jump"))
                Shortcut(listOf(commandKeyLabel, "S"), t("app.help.save_the_string_you_are_editing", "Save the string you are editing"))
                Shortcut(listOf(commandKeyLabel, "Enter"), t("app.help.save_and_go_to_the_next", "Save and go to the next one"))
                Shortcut(listOf("Esc"), t("app.help.close_whatever_is_open", "Close whatever is open"))
            }
        }

        // **The distinct treatment the spec asks for**, and the only badge on the page: this is
        // the card somebody arrives looking for when nothing else is working.
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionHeading(
                    title = t("app.help.talk_to_us", "Talk to us"),
                    description = t("app.help.a_person_reads_every_message_and", "A person reads every message, and this page works during an outage."),
                    icon = { LIconBadge { LMailIcon() } },
                )
                LMuted(
                    t("app.help.email_support_lingvo_dev_from_the_2", "Email support@lingvo.dev from the address on your account. Paste the details " +
                        "below into the message — they are what we would otherwise have to ask you " +
                        "for, and they save a round trip."),
                )
                // The design system's block rather than a hand-rolled `<pre>`: it is the same shape
                // as the install command further down, it reaches the keyboard as a scrollable
                // region, and it refuses to print anything shaped like a credential.
                LCodeBlock(
                    text = diagnostics.trim(),
                    label = t("app.help.support_details", "Support details"),
                    modifier = Modifier.fillMaxWidth(),
                    // The labelled button below is the copy affordance on this page; a second one
                    // in the header would be two controls for one job.
                    copyable = false,
                )
                Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    LButton(
                        if (copied) t("app.help.copied", "Copied") else t("app.help.copy_these_details", "Copy these details"),
                        {
                            scope.launch {
                                // **The promise was discarded and "Copied" was set regardless.**
                                // The clipboard API is unavailable on insecure origins and refused
                                // outright by some browsers, so the one page whose job is working
                                // when nothing else does reported a copy that never happened.
                                // `copySafely` throws rather than returning false, and carries the
                                // hidden-textarea fallback this had none of.
                                val ok = try {
                                    copySafely(diagnostics.trim())
                                    true
                                } catch (cancelled: CancellationException) {
                                    throw cancelled
                                } catch (_: Throwable) {
                                    false
                                }
                                copied = ok
                                copyFailed = !ok
                                if (ok) {
                                    // A receipt for one press, not a permanent state: latched, the
                                    // button read "Copied" for the rest of the page's life and a
                                    // second press gave no feedback at all.
                                    delay(COPY_FEEDBACK_MS)
                                    copied = false
                                }
                            }
                        },
                        variant = LButtonVariant.SECONDARY,
                    )
                }
                if (copyFailed) {
                    LAlert(
                        t("app.help.nothing_was_copied", "Nothing was copied"),
                        t("app.help.this_browser_refused_the_clipboard_select", "This browser refused the clipboard. Select the details above and copy " +
                            "them by hand, or write to us without them."),
                        variant = LAlertVariant.WARNING,
                        live = true,
                    )
                }
                LMuted(
                    t("app.help.never_send_us_an_api_token_2", "Never send us an API token or a provider key. We have no way to read either " +
                        "one and no reason to want it, and we will ask you to rotate any that " +
                        "reaches us."),
                )
            }
        }

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionTitle(t("app.help.what_we_can_and_cannot_do", "What we can and cannot do from our side"), icon = { LShieldIcon() })
                LMuted(
                    t("app.help.when_you_write_to_us_we_2", "When you write to us we can add machine translation credits to your " +
                        "workspace, end a translation run that is stuck and give back the credits " +
                        "it reserved, and end every sign-in and token belonging to one person if " +
                        "an account is compromised. Adding credits appears on your audit page, " +
                        "badged support, with the reason."),
                )
                LMuted(
                    t("app.help.we_cannot_sign_in_as_you_2", "We cannot sign in as you, and we cannot read your password, an API token, or " +
                        "a provider key — the first two exist only as hashes and the third is " +
                        "encrypted and returned by nothing, including our own tools. Nobody at " +
                        "Lingvo will ever ask you for one. A message that does is not from us, " +
                        "whatever address it appears to come from."),
                )
            }
        }

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionTitle(t("app.help.common_things", "Common things"), icon = { LBookOpenIcon() })
                Entry(
                    "/docs/quickstart",
                    t("app.help.getting_your_strings_in", "Getting your strings in"),
                    t("app.help.import_from_a_file_from_github", "Import from a file, from GitHub, or push from the CLI."),
                )
                Entry(
                    "/docs/cli",
                    t("app.help.the_command_line", "The command line"),
                    t("app.help.install_it_log_in_and_put", "Install it, log in, and put `lingvo qa --fail-on error` in your pipeline."),
                )
                Entry(
                    "/docs/formats",
                    t("app.help.formats_lingvo_reads", "Formats Lingvo reads"),
                    t("app.help.android_xml_apple_string_catalogs_and", "Android XML, Apple String Catalogs and .strings, JSON, YAML, gettext, " +
                        "XLIFF, Java properties."),
                )
                Entry(
                    "/app/settings/billing",
                    t("app.help.billing_and_plans", "Billing and plans"),
                    t("app.help.what_you_are_on_what_it", "What you are on, what it includes, and every invoice."),
                )
                Entry(
                    "/app/settings/usage",
                    t("app.help.credits_and_budgets", "Credits and budgets"),
                    t("app.help.what_machine_translation_has_cost_and", "What machine translation has cost, and how to cap it."),
                )
            }
        }

        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionTitle(t("app.help.install_the_cli", "Install the CLI"), icon = { LBracesIcon() })
                LMuted(t("app.help.one_line_no_package_manager_needed", "One line, no package manager needed:"))
                // A command to run, so it is copyable and scrolls rather than escaping its card at
                // phone widths — the hand-rolled `<pre>` did neither.
                LCodeBlock(
                    text = "curl -fsSL https://lingvo.dev/install.sh | sh",
                    label = t("app.help.install_command", "Install command"),
                    modifier = Modifier.fillMaxWidth(),
                )
                LMuted(t("app.help.or_with_homebrew_brew_install_merkost", "Or with Homebrew: brew install merkost/lingvo/lingvo"))
            }
        }

        LMuted(
            t("app.help.status_if_the_app_is_unreachable_2", "If the app is unreachable, check your connection and reload once. If the problem " +
                "continues, email support@lingvo.dev and include the support details above."),
        )

        LSectionTitle(t("app.help.if_lingvo_is_down_you_can", "If Lingvo is down, you can still ship"), icon = { LSparkleIcon() })
        LMuted(
            t("app.help.your_strings_are_files_in_your_2", "Pulled or exported translations are ordinary resource files. Keep them in your " +
                "repository so your app can read them without a Lingvo request at runtime."),
        )
        LMuted(
            t("app.help.so_an_outage_here_costs_you_2", "If Lingvo is unavailable, you can build with the files you already have. New pulls, " +
                "exports and hosted QA checks need the service; a CI job that requires them may be " +
                "blocked. Translations added since your last download will not be in your checkout yet."),
        )
    }
}

@Composable
private fun Entry(path: String, title: String, description: String) {
    Column(Modifier.fillMaxWidth().gap(2.px)) {
        Link(path = path) { Text(title) }
        LMuted(description)
    }
}

@Composable
private fun Shortcut(keys: List<String>, description: String) {
    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        LChord(keys)
        LMuted(description)
    }
}


private external fun encodeURIComponent(value: String): String
